import type { PreviewUser, PreviewEquipment } from './types';
import {
  MAX_FILE_SIZE,
  ACCEPTED_FILE_TYPES,
  ACCEPTED_FILE_EXTENSIONS,
} from './constants';

type TFunction = (key: string) => string;

/** Auto-incrementing key generator for stable React list keys */
let _keyCounter = 0;
export function generateKey(): string {
  return `item_${++_keyCounter}`;
}

/**
 * Validate uploaded file type and size
 */
export function validateFile(file: File, t?: TFunction): string | null {
  const extension = file.name.toLowerCase().slice(file.name.lastIndexOf('.'));

  if (
    !ACCEPTED_FILE_TYPES.includes(file.type) &&
    !ACCEPTED_FILE_EXTENSIONS.includes(extension)
  ) {
    return t?.('upload.errors.invalidType') ?? 'Invalid file type. Please upload an Excel file (.xlsx, .xls).';
  }

  if (file.size > MAX_FILE_SIZE) {
    return t?.('upload.errors.tooLarge') ?? 'File too large. Maximum size is 5MB.';
  }

  return null;
}

/**
 * Find duplicate emails in users list
 */
export function getDuplicateEmails(users: PreviewUser[]): Set<string> {
  const emails = users.map((u) => u.email.toLowerCase().trim()).filter(Boolean);
  const seen = new Set<string>();
  const duplicates = new Set<string>();

  for (const email of emails) {
    if (seen.has(email)) {
      duplicates.add(email);
    }
    seen.add(email);
  }

  return duplicates;
}

/**
 * Check if a specific user email is a duplicate
 */
export function isEmailDuplicate(
  email: string,
  users: PreviewUser[],
  currentIndex: number
): boolean {
  const normalizedEmail = email.toLowerCase().trim();
  if (!normalizedEmail) return false;

  return users.some(
    (u, i) => i !== currentIndex && u.email.toLowerCase().trim() === normalizedEmail
  );
}

/**
 * Find duplicate equipment names in equipment list
 */
export function getDuplicateEquipmentNames(
  equipment: PreviewEquipment[]
): Set<string> {
  const names = equipment.map((e) => e.name.toLowerCase().trim()).filter(Boolean);
  const seen = new Set<string>();
  const duplicates = new Set<string>();

  for (const name of names) {
    if (seen.has(name)) {
      duplicates.add(name);
    }
    seen.add(name);
  }

  return duplicates;
}

/**
 * Check if a specific equipment name is a duplicate
 */
export function isEquipmentNameDuplicate(
  name: string,
  equipment: PreviewEquipment[],
  currentIndex: number
): boolean {
  const normalizedName = name.toLowerCase().trim();
  if (!normalizedName) return false;

  return equipment.some(
    (e, i) => i !== currentIndex && e.name.toLowerCase().trim() === normalizedName
  );
}

/**
 * Check if user row has validation warnings
 */
export function hasUserWarning(user: PreviewUser): boolean {
  return !user.email.trim();
}

/**
 * Check if equipment row has validation warnings
 */
export function hasEquipmentWarning(equipment: PreviewEquipment): boolean {
  return !equipment.name.trim();
}

/**
 * Count entities in preview data
 */
export function getEntityCounts(
  users: PreviewUser[],
  equipment: PreviewEquipment[]
): { users: number; equipment: number; alertContacts: number } {
  return {
    users: users.length,
    equipment: equipment.length,
    alertContacts: users.filter((u) => u.isAlertContact).length,
  };
}

/**
 * Check if any equipment has invalid threshold ranges (min >= max)
 */
export function hasInvalidThresholds(equipment: PreviewEquipment[]): boolean {
  return equipment.some(
    (e) =>
      e.thresholdMin !== null &&
      e.thresholdMax !== null &&
      e.thresholdMin >= e.thresholdMax
  );
}

/**
 * Check if preview data has any duplicates
 */
export function hasDuplicates(
  users: PreviewUser[],
  equipment: PreviewEquipment[]
): boolean {
  return (
    getDuplicateEmails(users).size > 0 ||
    getDuplicateEquipmentNames(equipment).size > 0
  );
}
